import { useCallback, useEffect } from "react";
import { authNetConfig, isCardPaymentConfigured } from "@/config/paymentConfig";

// Accept.js (Authorize.Net's in-page tokenization SDK, v1/Accept.js) integration for
// bank-account/ACH capture (#296). A SEPARATE vendor script from v3/AcceptUI.js (see
// useAcceptUi): it defines `window.Accept` and nothing else — no DOM binding, no host button,
// no overlay, no scrollTo — so unlike AcceptUI there is no readyState dance and no global
// response handler to own. `Accept.dispatchData` sends the bank details browser → Authorize.Net
// directly and invokes a per-call handler with the same response shape AcceptUI uses.
//
// PCI/NACHA discipline: the routing/account numbers transit the CALLER's form state and the
// dispatchData payload only. This hook keeps nothing after the call — the resolved capture
// carries just the opaque nonce and a last4 display fragment computed before the vendor was
// ever contacted. Nothing here is logged, stored in atoms, or sent to our API.
//
// The script is injected lazily, exactly once per document, with the same guard pattern as
// useAcceptUi: not because Accept.js damages other pages (it doesn't), but because a second
// injection re-runs the vendor IIFE for no benefit and the one-injection invariant keeps both
// vendor scripts under a single, identical discipline.

export interface AcceptJsMessage {
    code: string;
    text: string;
}

// Shape of the object Accept.js passes to the dispatchData handler — mirrors AcceptUI's
// (bank captures never carry encryptedCardData).
export interface AcceptJsResponse {
    messages: { resultCode: string; message: AcceptJsMessage[] };
    opaqueData?: { dataDescriptor: string; dataValue: string };
}

// The vendor's eCheck account types, in the vendor's own casing.
export type AcceptJsAccountType = "checking" | "savings" | "businessChecking";

export interface AcceptJsBankData {
    accountNumber: string;
    routingNumber: string;
    nameOnAccount: string;
    accountType: AcceptJsAccountType;
}

// The vendor's exact request shape. `apiLoginID` (capital ID) is the vendor's casing, not a
// typo — Accept.js silently ignores unknown keys, so our config's `apiLoginId` spelling would
// fail only at dispatch time with an auth error. Pinned by useAcceptJs.test.ts.
export interface AcceptJsPayload {
    authData: { apiLoginID: string; clientKey: string };
    bankData: AcceptJsBankData;
}

// Successful capture routed back to the consumer. `token` is the single-use nonce encoded for
// our API as "<dataDescriptor>|<dataValue>" — identical to the card path (see
// CreateOrderRequest.vendorPaymentToken). `accountLast4` is display data computed on OUR side
// before tokenization; the vendor returns no masked account details.
export interface AcceptJsBankCapture {
    token: string;
    accountLast4: string;
}

declare global {
    interface Window {
        Accept?: {
            dispatchData: (
                payload: AcceptJsPayload,
                handler: (response: AcceptJsResponse) => void,
            ) => void;
        };
    }
}

// Shown to shoppers. Anything a developer needs (env var names, script state) goes to
// console.error instead — a payment surface must not display internal configuration detail.
const GENERIC_UNAVAILABLE =
    "We couldn't load the secure payment form. Please refresh and try again.";
const FALLBACK_ERROR =
    "Your bank details could not be verified. Please check them and try again.";
// dispatchData never called back — see TOKENIZE_TIMEOUT_MS.
const TIMEOUT_ERROR =
    "We couldn't reach the payment service to verify your bank details. Please check your connection and try again.";

// The vendor call is a browser → Authorize.Net XHR we have no other visibility into: its
// handler is the ONLY thing that settles the promise, so a request that never comes back
// (blocked by a corporate proxy or an ad-blocker, or the vendor throwing before it invokes
// the callback) would otherwise hang the caller's submit state forever, with the form's
// only submit button stuck disabled. Generous enough never to pre-empt a slow-but-live
// call; the tokenization itself is normally sub-second.
const TOKENIZE_TIMEOUT_MS = 30_000;

// Marks the element we injected, so the guard below can be checked against the document.
// acceptJsMock.ts hard-codes the matching selector as a literal — see the rationale there.
const SCRIPT_MARKER = "data-acceptjs";

// Exactly one injection per document, no matter how many consumers mount or how often —
// also the correct mechanism under StrictMode, whose mount→unmount→mount double-invoke would
// otherwise inject twice. The injected element is the source of truth (the promise alone
// could outlive it).
let scriptReady: Promise<void> | null = null;

const loadAcceptJs = (): Promise<void> => {
    if (scriptReady && document.querySelector(`script[${SCRIPT_MARKER}]`)) {
        return scriptReady;
    }

    scriptReady = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = authNetConfig.acceptUrl;
        script.charset = "utf-8";
        script.setAttribute(SCRIPT_MARKER, "");

        // Unlike AcceptUI, "load" IS the readiness signal: Accept.js defines
        // window.Accept.dispatchData synchronously while the script executes, with no
        // deferred document.onreadystatechange binding to wait out.
        script.addEventListener("load", () => resolve());

        script.addEventListener("error", () => {
            console.error(
                `Accept.js failed to load from "${authNetConfig.acceptUrl}" (VITE_AUTHNET_ACCEPT_URL). ` +
                    "Bank-account capture is unavailable — check the URL, the network, and that the page is served over HTTPS.",
            );
            // Clear the guard so a later attempt can retry the download. Drop the dead element
            // with it: the guard's invariant is that the injected element IS the state, and a
            // stale element left in <head> would still match the query above while the promise
            // has already been discarded.
            scriptReady = null;
            script.remove();
            reject(new Error("Accept.js script failed to load."));
        });

        document.head.appendChild(script);
    });

    return scriptReady;
};

export const useAcceptJs = () => {
    // Two legs: the shared key predicate, plus this hook's own script URL (optional env key
    // VITE_AUTHNET_ACCEPT_URL) — with keys but no URL there is nothing to inject.
    const isConfigured =
        isCardPaymentConfigured() && Boolean(authNetConfig.acceptUrl);

    useEffect(() => {
        // Start the download as soon as a payment surface mounts so tokenization is ready by
        // the time the user submits. An unconfigured environment never contacts the vendor.
        // A failure here is surfaced when tokenizeBankAccount retries the load.
        if (!isConfigured) return;
        void loadAcceptJs().catch(() => {
            /* surfaced by tokenizeBankAccount */
        });
    }, [isConfigured]);

    const tokenizeBankAccount = useCallback(
        async (bankData: AcceptJsBankData): Promise<AcceptJsBankCapture> => {
            // Computed BEFORE the vendor call: after this line nothing on our side needs the
            // full account number again — it lives only in the caller's form state and the
            // dispatchData payload below.
            const accountLast4 = bankData.accountNumber.slice(-4);

            try {
                // Awaits the injection promise, so a submit landing before the download
                // finishes still tokenizes instead of failing; also the retry path after an
                // earlier load failure cleared the guard.
                await loadAcceptJs();
            } catch {
                // Detail already went to console.error in the load handler.
                throw new Error(GENERIC_UNAVAILABLE);
            }

            return new Promise<AcceptJsBankCapture>((resolve, reject) => {
                // The vendor gives no cancellation handle, so the timeout can't stop an
                // in-flight dispatchData — it only stops US waiting. `settled` makes the
                // race one-shot in both directions: a late vendor callback after the
                // timeout is ignored (it must never resolve a capture the shopper has
                // already been told failed), and the timer is cleared on a normal settle.
                let settled = false;

                // Armed before the guard below so `settle` can close over it; the guard's own
                // early exit clears it immediately, so nothing is left running.
                const timeoutId = setTimeout(() => {
                    console.error(
                        `Accept.js dispatchData did not call back within ${TOKENIZE_TIMEOUT_MS}ms — the vendor request may be blocked (proxy/ad-blocker) or the script may have thrown internally.`,
                    );
                    settle(() => reject(new Error(TIMEOUT_ERROR)));
                }, TOKENIZE_TIMEOUT_MS);

                const settle = (outcome: () => void) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timeoutId);
                    outcome();
                };

                const accept = window.Accept;
                if (!accept) {
                    // Load fired but the global never appeared — a mis-pointed
                    // VITE_AUTHNET_ACCEPT_URL serving some other valid script.
                    console.error(
                        "Accept.js loaded but window.Accept is not defined — check VITE_AUTHNET_ACCEPT_URL points at v1/Accept.js.",
                    );
                    settle(() => reject(new Error(GENERIC_UNAVAILABLE)));
                    return;
                }

                try {
                    accept.dispatchData(
                        {
                            authData: {
                            // Vendor casing — see AcceptJsPayload.
                                apiLoginID: authNetConfig.apiLoginId,
                                clientKey: authNetConfig.clientKey,
                            },
                            bankData,
                        },
                        (response) => {
                            if (response.messages?.resultCode === "Ok") {
                                if (response.opaqueData) {
                                    const { dataDescriptor, dataValue } =
                                    response.opaqueData;
                                    settle(() =>
                                        resolve({
                                            token: `${dataDescriptor}|${dataValue}`,
                                            accountLast4,
                                        }),
                                    );
                                    return;
                                }
                                // Ok with no nonce: the vendor's message text is a SUCCESS string
                                // ("Successful."), which must never be shown to a shopper as the
                                // reason their payment failed.
                                console.error(
                                    "Accept.js returned resultCode Ok without opaqueData.",
                                    response.messages,
                                );
                                settle(() => reject(new Error(FALLBACK_ERROR)));
                                return;
                            }
                            settle(() =>
                                reject(
                                    new Error(
                                        response.messages?.message?.[0]?.text ??
                                        FALLBACK_ERROR,
                                    ),
                                ),
                            );
                        },
                    );
                } catch (vendorFailure) {
                    // A synchronous throw out of the vendor SDK. Without this the raw
                    // vendor error would escape as the promise's rejection and PaymentForm
                    // would render its `.message` — internal vendor text shown to a
                    // shopper as the reason their payment failed. Also clears the timer,
                    // which would otherwise sit armed for the full timeout.
                    console.error(
                        "Accept.js dispatchData threw synchronously.",
                        vendorFailure,
                    );
                    settle(() => reject(new Error(FALLBACK_ERROR)));
                }
            });
        },
        [],
    );

    return { isConfigured, tokenizeBankAccount };
};
